import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Loader,
  ErrorFallback,
  TextInput,
  isValidate,
  isFromValid,
  DescriptionTextArea,
  InputSwitch,
  DropDownInput,
} from 'smart-react';
import { Card, CardHeader, CardBody } from '@progress/kendo-react-layout';
import { Error } from '@progress/kendo-react-labels';
import { Link } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { buildNotification, GenerateNotification } from 'smart-react';
import {
  createDataType,
  updateDataType,
  getDataType,
} from '../Services/DataTypeService';
import { base64ToObject } from '../../../../Utils/Common/commonUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import NoRecordFound from '../../../Core/Components/NoRecordFound/NoRecordFound';
import useQueryParams from '../../../Core/CustomHooks/useQueryParams';
import { Form, Field } from '@progress/kendo-react-form';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../../constants/eventDataTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { SplitButton, SplitButtonItem } from '@progress/kendo-react-buttons';
import { retrieveAndDecrypt } from '../../../../Utils/Storage/localStorageUtil';
import { LOOKUP_TYPES } from '../../../../constants/applicationConstants';
import {
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
  ERROR_MESSAGE,
} from '../../../../constants/notificationMessages';
/**
 * Add New Setting Data Type main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Add New Setting Data Type component.
 */
const AddSettingDataType = () => {
  const navigate = useNavigate();
  const { getParam } = useQueryParams();
  const refId = getParam('refId');
  const [detailsDataModified, setDetailsDataModified] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [isEditor, setIsEditor] = React.useState(false);
  const [isTextArea, setIsTextArea] = React.useState(false);
  const [validateForm, setValidateForm] = React.useState(false);
  const [noRecord, setNoRecord] = React.useState(false);
  const baseTypes = retrieveAndDecrypt(LOOKUP_TYPES?.baseTypes);
  const UIControls = retrieveAndDecrypt(LOOKUP_TYPES?.uiControls);
  const [formData, setFormData] = React.useState({
    DataType: '',
    BaseType: '',
    Label: '',
    Placeholder: '',
    UiControl: '',
    DefaultValue: '',
    HelpText: '',
    Regex: '',
    MinLen: '',
    MaxLen: '',
    MinValue: '',
    MaxValue: '',
    Active: true,
  });
  const requiredFields = ['DataType'];
  const [editorField, setEditorField] = React.useState({
    name: '',
    value: '',
    label: '',
  });
  const { state } = useLocation();
  const [tempState, setTempState] = React.useState(state);
  const handleSaveSplit = (e) => {
    const { itemIndex } = e;
    switch (itemIndex) {
      case 0: {
        onSave({ isExit: true });
        break;
      }
      case 1:
        onSave({ isContinue: true });
      default: {
      }
    }
  };

  // React.useEffect(() => {
  //   if (state) {
  //     const { dataSet } = state;
  //     (async () => {
  //       setLoading(true);
  //       const response = await getDataType({
  //         data: dataSet,
  //       });
  //       const testData = response.Payload;
  //       setFormData(testData);
  //       setLoading(false);
  //     })();
  //     setFormData({ ...dataSet });
  //     setDetailsDataModified(true);
  //   } else {
  //     setDetailsDataModified(true);
  //   }
  // }, [state]);

  /**
   * Get Data Type Detail from API for edit Data Type
   * @param {*} stateData
   */
  const fetchDataTypeData = async (stateData) => {
    setLoading(true);
    try {
      let refObj = base64ToObject(refId);
      const response = await getDataType({
        data: refObj,
      });
      const testData = response.Payload;
      if (testData) {
        setTempState({ ...stateData, dataSet: testData, edit: true });
        setFormData(testData);
        setNoRecord(false);
        setDetailsDataModified(true);
      } else {
        setNoRecord(true);
      }
    } catch (error) {
      // Handle error state if needed
      setNoRecord(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (tempState || (refId && refId !== 'new')) {
      const dataSet = state ? state.dataSet : null;
      // Call the async function
      fetchDataTypeData(state);

      if (dataSet) {
        // Rest of the synchronous logic
        setFormData({ ...dataSet });
        setDetailsDataModified(true);
      }
    } else {
      setDetailsDataModified(true);
    }
  }, [state]);

  /**
   * handle Input Change
   * @e object
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  /**
   * onSave
   * submit the test data to api
   */
  const onSave = async ({ isExit = false }) => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }
    setLoading(true);
    let notificationMetaData = {};
    const data = {
      ...formData,
    };
    let response;
    let method;
    if (tempState?.edit) {
      method = updateDataType;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createDataType;
      notificationMetaData = CREATE_MESSAGE;
    }

    try {
      setValidateForm(false);
      response = await method({
        data,
        isServiceWorker: false,
      });

      if (!response.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        if (!tempState?.edit) {
          setFormData({
            DataType: '',
            BaseType: '',
            Label: '',
            Placeholder: '',
            UiControl: '',
            DefaultValue: '',
            HelpText: '',
            Regex: '',
            MinLen: '',
            MaxLen: '',
            MinValue: '',
            MaxValue: '',
            Active: true,
          });
        }
        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        if (isExit) {
          navigate('/admin/data-types');
        }
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * onExit
   * back to tests listing screen
   */
  const onExit = () => {
    navigate('/admin/data-types');
  };
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className='container mx-auto'>
        <React.Suspense fallback={<Loader />}>
          {loading && <Loader />}
          {noRecord ? (
            <NoRecordFound
              recordNotFoundText={'No Data found'}
              onExit={onExit}
            />
          ) : (
            <>
              <div className={`${isTextArea ? '' : 'k-d-none'}`}>
                <DescriptionTextArea
                  onChange={handleInputChange}
                  label={'Description'}
                  renderTypes={!detailsDataModified ? 'ReadOnly' : ''}
                  validateForm={validateForm}
                  formData={formData}
                  setFormData={setFormData}
                  detailsDataModified={detailsDataModified}
                  isTextArea={isTextArea}
                  setIsTextArea={setIsTextArea}
                  editorField={editorField}
                  setEditorField={setEditorField}
                  row={2}
                />
              </div>
              <div
                className={`add-test main-page-section add-page-section ${!isEditor ? '' : 'k-d-none'}`}
              >
                <Card className='tophead'>
                  <CardHeader>
                    <div className='action-buttons-container'>
                      <Link
                        className='action-icon-wrapper'
                        to={`/admin/data-types`}
                      >
                        <FontAwesomeIcon
                          className='k-mr-2'
                          icon={faArrowLeft}
                        />
                      </Link>
                      <p className='k-align-items-center k-d-flex k-mb-0 display-text'>
                        Data Type: {formData?.DataType}
                      </p>
                    </div>
                  </CardHeader>
                </Card>
                <Card className='k-mt-3 detail-card'>
                  <CardHeader>
                    <h6 className='card-title-secondary'>Details</h6>
                  </CardHeader>
                  <CardBody className='detail-card-body'>
                    <div>
                      <Form
                        onSubmit={onSave}
                        render={(formRenderProps) => (
                          <div className='card-content'>
                            <div className='k-d-flex single-field-row'>
                              <div
                                className={`field-wrapper ${isValidate(validateForm, formData.DataType) ? 'field-invalid' : 'field-valid'}`}
                              >
                                <Field
                                  key={'DataType'}
                                  id={'DataType'}
                                  name={'DataType'}
                                  label={'Type'}
                                  data={formData?.DataType}
                                  onChange={handleInputChange}
                                  type={'text'}
                                  component={TextInput}
                                  renderTypes={
                                    tempState?.edit ? 'ReadOnly' : ''
                                  }
                                  optional={false}
                                  requiredField={true}
                                />
                                {isValidate(
                                  validateForm,
                                  formData.DataType,
                                ) && <Error>This field cannot be empty</Error>}
                              </div>
                              <div className={`field-wrapper`}>
                                <DropDownInput
                                  label='Base Type'
                                  textField='DisplayName'
                                  dataItemKey='Value'
                                  name={'BaseType'}
                                  dataSet={baseTypes}
                                  value={
                                    formData?.BaseType
                                      ? {
                                          Value: formData?.BaseType,
                                          DisplayName: formData?.BaseType,
                                        }
                                      : null
                                  }
                                  handleChange={(e) => {
                                    setFormData((prevState) => ({
                                      ...prevState,
                                      BaseType: e.value?.Value,
                                    }));
                                  }}
                                  defaultItem={{
                                    DisplayName: 'Select Base Type ...',
                                    Value: '',
                                  }}
                                  filterable={true}
                                />
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div className={`field-wrapper`}>
                                <Field
                                  key={'Label'}
                                  id={'Label'}
                                  name={'Label'}
                                  label={'Label'}
                                  data={formData?.Label}
                                  onChange={handleInputChange}
                                  type={'text'}
                                  component={TextInput}
                                  optional={false}
                                />
                              </div>
                              <div className={`field-wrapper`}>
                                <Field
                                  key={'Placeholder'}
                                  id={'Placeholder'}
                                  name={'Placeholder'}
                                  label={'Place Holder'}
                                  data={formData?.Placeholder}
                                  onChange={handleInputChange}
                                  type={'text'}
                                  component={TextInput}
                                  optional={false}
                                />
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div className={`field-wrapper`}>
                                <DropDownInput
                                  label='Ui Control'
                                  textField='DisplayName'
                                  dataItemKey='Value'
                                  name={'UiControl'}
                                  dataSet={UIControls}
                                  value={
                                    formData?.UiControl
                                      ? {
                                          Value: formData?.UiControl,
                                          DisplayName: formData?.UiControl,
                                        }
                                      : null
                                  }
                                  handleChange={(e) => {
                                    setFormData((prevState) => ({
                                      ...prevState,
                                      UiControl: e.value?.Value,
                                    }));
                                  }}
                                  defaultItem={{
                                    DisplayName: 'Select Base Type ...',
                                    Value: '',
                                  }}
                                  filterable={true}
                                />
                              </div>
                              <div className={`field-wrapper`}>
                                <Field
                                  key={'DefaultValue'}
                                  id={'DefaultValue'}
                                  name={'DefaultValue'}
                                  label={'Default Value'}
                                  data={formData?.DefaultValue}
                                  onChange={handleInputChange}
                                  type={'text'}
                                  component={TextInput}
                                  optional={false}
                                />
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div className={`field-wrapper`}>
                                <Field
                                  key={'HelpText'}
                                  id={'HelpText'}
                                  name={'HelpText'}
                                  label={'Help Text'}
                                  data={formData?.HelpText}
                                  onChange={handleInputChange}
                                  type={'text'}
                                  component={TextInput}
                                  optional={false}
                                />
                              </div>
                              <div className={`field-wrapper`}>
                                <Field
                                  key={'Regex'}
                                  id={'Regex'}
                                  name={'Regex'}
                                  label={'Regex'}
                                  data={formData?.Regex}
                                  onChange={handleInputChange}
                                  type={'text'}
                                  component={TextInput}
                                  optional={false}
                                />
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div className={`field-wrapper`}>
                                <Field
                                  key={'MinLen'}
                                  id={'MinLen'}
                                  name={'MinLen'}
                                  label={'Min Length'}
                                  data={formData?.MinLen}
                                  onChange={handleInputChange}
                                  type={'number'}
                                  component={TextInput}
                                  optional={false}
                                />
                              </div>
                              <div className={`field-wrapper`}>
                                <Field
                                  key={'MaxLen'}
                                  id={'MaxLen'}
                                  name={'MaxLen'}
                                  label={'Max Length'}
                                  data={formData?.MaxLen}
                                  onChange={handleInputChange}
                                  type={'number'}
                                  component={TextInput}
                                  optional={false}
                                />
                              </div>
                            </div>
                            <div className='k-d-flex single-field-row'>
                              <div className={`field-wrapper`}>
                                <Field
                                  key={'MinValue'}
                                  id={'MinValue'}
                                  name={'MinValue'}
                                  label={'Min Value'}
                                  data={formData?.MinValue}
                                  onChange={handleInputChange}
                                  type={'number'}
                                  component={TextInput}
                                  optional={false}
                                />
                              </div>
                              <div className={`field-wrapper`}>
                                <Field
                                  key={'MaxValue'}
                                  id={'MaxValue'}
                                  name={'MaxValue'}
                                  label={'Max Value'}
                                  data={formData?.MaxValue}
                                  onChange={handleInputChange}
                                  type={'number'}
                                  component={TextInput}
                                  optional={false}
                                />
                              </div>
                            </div>
                            <div className='single-field-row k-mt-4 single-row-60'>
                              <div className={`field-wrapper`}>
                                <Field
                                  name='Active'
                                  component={InputSwitch}
                                  label='Active'
                                  onLabel='Yes'
                                  offLabel='No'
                                  id='Active'
                                  checked={formData?.Active}
                                  onChange={handleInputChange}
                                  disabled={!detailsDataModified}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                  </CardBody>
                </Card>
                {detailsDataModified && (
                  <Card className='k-mt-3'>
                    <CardHeader>
                      <div className='k-d-flex single-field-row button-form-wrapper k-action-buttons !k-px-0'>
                        <>
                          <Button
                            icon={'cancel'}
                            onClick={() => onExit()}
                            type='button'
                          >
                            Cancel
                          </Button>
                          <SplitButton
                            text='save'
                            themeColor={'primary'}
                            onButtonClick={() => onSave({ isExit: false })}
                            onItemClick={handleSaveSplit}
                          >
                            <SplitButtonItem text='Save & Close' />
                          </SplitButton>
                        </>
                      </div>
                    </CardHeader>
                  </Card>
                )}
              </div>
            </>
          )}
        </React.Suspense>
      </div>
    </ErrorBoundary>
  );
};
export default AddSettingDataType;
